import { Instant } from "./JSJodaJSON";
import {
  AgencyId,
  CaregiverDocumentTypeId,
  CaregiverNotificationId,
  CareMomentsAdjustmentId,
  ChatRoomId,
  ClusterId,
  CommCenterTicketId,
  PatientId,
  PatientTaskInstanceId,
  VisitBroadcastId,
  VisitInstanceId,
} from "./ObjectIds";
import { OnboardingCaregiversNotificationPayloads } from "./OnboardingCaregiversWorker";

/**
 * The Push Notifications that are sent to the app
 */
export const NotificationType = {
  VISIT_ASSIGNMENT_NOTIFICATION: "VISIT_ASSIGNMENT_NOTIFICATION" as const,
  VISIT_CANCEL_NOTIFICATION: "VISIT_CANCEL_NOTIFICATION" as const,
  CAREGIVER_STATUS_CHANGED: "CAREGIVER_STATUS_CHANGED" as const,
  VISIT_CHAT_MESSAGE: "VISIT_CHAT_MESSAGE" as const,
  AGENCY_MEMBER_CHAT_MESSAGE: "AGENCY_MEMBER_CHAT_MESSAGE" as const,
  PATIENT_CHAT_MESSAGE: "PATIENT_CHAT_MESSAGE" as const,
  VISIT_NOTIFICATION: "VISIT_NOTIFICATION" as const,
  VISIT_INSTANCE_NOTIFICATION: "VISIT_INSTANCE_NOTIFICATION" as const,
  NEW_CLUSTER_NOTIFICATION: "NEW_CLUSTER_NOTIFICATION" as const,
  AVAILABILITY_ANOMALY_NOTIFICATION: "AVAILABILITY_ANOMALY_NOTIFICATION" as const,
  ORIENTATION_VIDEO_REMINDER_NOTIFICATION: "ORIENTATION_VIDEO_REMINDER_NOTIFICATION" as const,
  COMPLIANCE_PROBLEM_NOTIFICATION: "COMPLIANCE_PROBLEM_NOTIFICATION" as const,
  VISIT_AWAITING_PATIENT_CONTACT: "VISIT_AWAITING_PATIENT_CONTACT" as const,
  CAREGIVER_GOT_RECRUITED: "CAREGIVER_GOT_RECRUITED" as const,
  RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION:
    "RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION" as const,
  CAREGIVER_MISSING_DOCUMENTS_REMINDER: "CAREGIVER_MISSING_DOCUMENTS_REMINDER" as const,
  COVID_19_TRAINING_REMINDER: "COVID_19_TRAINING_REMINDER" as const,
  COVID_19_SCREENING_REMINDER: "COVID_19_SCREENING_REMINDER" as const,
  COVID_19_VISIT_SCREENING_REMINDER: "COVID_19_VISIT_SCREENING_REMINDER" as const,
  CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED:
    "CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED" as const,
  CONVERT_IMPORTED_VISIT_REQUEST_RESULT: "CONVERT_IMPORTED_VISIT_REQUEST_RESULT" as const,
  PATIENT_TASK_NOTIFICATION: "PATIENT_TASK_NOTIFICATION" as const,
  CAREGIVER_INACTIVE_FOR_3_WEEKS: "CAREGIVER_INACTIVE_FOR_3_WEEKS" as const,
  TASK_WASNT_SCHEDULED: "TASK_WASNT_SCHEDULED" as const,
  MISSED_CLOCK_IN: "MISSED_CLOCK_IN" as const,
  MISSED_CLOCK_OUT: "MISSED_CLOCK_OUT" as const,
  FORMS_AWAITING_SUBMISSION_VISIT: "FORMS_AWAITING_SUBMISSION_VISIT" as const,
  COMM_CENTER_TICKET_MESSAGE: "COMM_CENTER_TICKET_MESSAGE" as const,
  ONBOARDING_CAREGIVER_ENGAGEMENTS: "ONBOARDING_CAREGIVER_ENGAGEMENTS" as const,
  ONBOARDING_CAREGIVER_ENGAGEMENTS2: "ONBOARDING_CAREGIVER_ENGAGEMENTS2" as const,
  GENERAL_VISIT_ASSIGNMENT_NOTIFICATION: "GENERAL_VISIT_ASSIGNMENT_NOTIFICATION" as const,
  CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED: "CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED" as const,
  RAFFLE_ENGAGEMENT: "RAFFLE_ENGAGEMENT" as const,
};

export type NotificationType =
  | "CAREGIVER_STATUS_CHANGED"
  | "VISIT_CHAT_MESSAGE"
  | "AGENCY_MEMBER_CHAT_MESSAGE"
  | "VISIT_STARTING_SOON_REMINDER"
  | "VISIT_NOTIFICATION"
  | "VISIT_INSTANCE_NOTIFICATION"
  | "VISIT_CANCEL_NOTIFICATION"
  | "VISIT_ASSIGNMENT_NOTIFICATION"
  | "PATIENT_CHAT_MESSAGE"
  | "NEW_CLUSTER_NOTIFICATION"
  | "AVAILABILITY_ANOMALY_NOTIFICATION"
  | "ORIENTATION_VIDEO_REMINDER_NOTIFICATION"
  | "COMPLIANCE_PROBLEM_NOTIFICATION"
  | "VISIT_AWAITING_PATIENT_CONTACT"
  | "CAREGIVER_GOT_RECRUITED"
  | "RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION"
  | "CAREGIVER_MISSING_DOCUMENTS_REMINDER"
  | "PATIENT_DOCUMENT_UNSUBMITTED_REMINDER_NOTIFICATION"
  | "COVID_19_TRAINING_REMINDER"
  | "COVID_19_SCREENING_REMINDER"
  | "COVID_19_VISIT_SCREENING_REMINDER"
  | "CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED"
  | "CONVERT_IMPORTED_VISIT_REQUEST_RESULT"
  | "PATIENT_TASK_NOTIFICATION"
  | "PATIENT_ON_VACATION"
  | "COMM_CENTER_TICKET_MESSAGE"
  | "ONBOARDING_CAREGIVER_ENGAGEMENTS2"
  | "INSTA_PAY_FAILURE_NOTIFICATION"
  | "FLEXIBLE_VISIT_BROADCAST_DETAILS_CHANGED"
  | "CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED"
  | "RAFFLE_ENGAGEMENT"
  | NotificationTypeWithoutPayLoad;

export type NotificationTypeWithoutPayLoad =
  | "CAREGIVER_INACTIVE_FOR_3_WEEKS"
  | "FORMS_AWAITING_SUBMISSION_TASK"
  | "TASK_WASNT_SCHEDULED"
  | "MISSED_CLOCK_IN"
  | "MISSED_CLOCK_OUT"
  | "FORMS_AWAITING_SUBMISSION_VISIT"
  | "EXPIRED_COMPLIANCE_ITEM"
  | "ABOUT_TO_EXPIRE_COMPLIANCE_ITEM"
  | "CAREGIVER_VISIT_NOT_SEEN"
  | "DUTY_SHEET_SUBMISSION_REMINDER"
  | "ONBOARDING_REMINDER"
  | "NEW_ONBOARDING_VISITS"
  | "ONBOARDING_DOCUMENT_NOTIFICATION"
  | "ONBOARDING_CAREGIVER_ENGAGEMENTS"
  | "GENERAL_VISIT_ASSIGNMENT_NOTIFICATION"
  | "VISIT_BROADCAST_STOPPED_BY_AGENCY_MEMBER";

export function notificationTypeHasEvents(type: NotificationType): boolean {
  const notificationTypesWithEvents: NotificationType[] = [
    NotificationType.VISIT_NOTIFICATION,
    NotificationType.CAREGIVER_STATUS_CHANGED,
    NotificationType.VISIT_CANCEL_NOTIFICATION,
    NotificationType.ONBOARDING_CAREGIVER_ENGAGEMENTS,
    NotificationType.ONBOARDING_CAREGIVER_ENGAGEMENTS2,
    NotificationType.GENERAL_VISIT_ASSIGNMENT_NOTIFICATION,
  ];
  return notificationTypesWithEvents.indexOf(type) > -1;
}

export type NotificationData =
  | NotificationData.CaregiverStatusChanged
  | NotificationData.AgencyMemberChatMessage
  | NotificationData.VisitChatMessage
  | NotificationData.VisitStartingSoonReminder
  | NotificationData.VisitNotification
  | NotificationData.PatientChatMessage
  | NotificationData.VisitInstanceNotification
  | NotificationData.VisitAssignmentNotification
  | NotificationData.VisitCancelNotification
  | NotificationData.ClusterNotification
  | NotificationData.AvailabilityAnomalyNotification
  | NotificationData.OrientationVideoReminderNotification
  | NotificationData.ComplianceProblemNotification
  | NotificationData.VisitAwaitingPatientContact
  | NotificationData.CaregiverGotRecruited
  | NotificationData.RecruitmentApplicationReminder
  | NotificationData.CaregiverMissingDocumentReminder
  | NotificationData.PatientDocumentUnsubmittedRemainder
  | NotificationData.Covid19TrainingReminder
  | NotificationData.Covid19ScreeningReminder
  | NotificationData.Covid19VisitScreeningReminder
  | NotificationData.CaregiverPassportDocumentVerificationStatusChanged
  | NotificationData.ConvertImportedVisitRequestResult
  | NotificationData.PatientTaskNotification
  | NotificationData.CaregiverInactiveFor3Weeks
  | NotificationData.FormsAwaitingSubmissionTask
  | NotificationData.TaskWasntScheduled
  | NotificationData.MissedClockIn
  | NotificationData.MissedClockOut
  | NotificationData.FormsAwaitingSubmissionVisit
  | NotificationData.ExpiredComplianceItem
  | NotificationData.AboutToExpireComplianceItem
  | NotificationData.CaregiverVisitNotSeen
  | NotificationData.PatientOnVacation
  | NotificationData.DutySheetSubmissionReminder
  | NotificationData.OnboardingReminder
  | NotificationData.NewOnboardingVisits
  | NotificationData.OnboardingDocumentNotification
  | NotificationData.CommCenterTicketMessage
  | NotificationData.OnboardingCaregiverEngagements
  | NotificationData.OnboardingCaregiverEngagements2
  | NotificationData.GeneralVisitAssignmentNotification
  | NotificationData.InstaPayFailureNotification
  | NotificationData.VisitBroadcastStoppedByAgencyMember
  | NotificationData.FlexibleVisitBroadcastDetailsChanged
  | NotificationData.CareMomentsCaregiverRankAdjusted
  | NotificationData.RaffleEngagement;

export interface FcmPushNotification {
  id?: CaregiverNotificationId;
  data: NotificationData;
}

export interface CaregiverNotificationSettings {
  /**
   * If this is `false` then absolutely no push notifications will be sent. If
   * it is true then the other settings apply
   */
  pushNotificationsEnabled: boolean;

  categories: CaregiverNotificationSettingsCategories;
}

export interface CaregiverNotificationSettingsCategories {
  newVisitsAvailable: boolean;
  agencyNotifications: boolean;
  chatMessages: boolean;
  trainingCenterReminders: boolean;
  myVisitsChanges: boolean;
  myVisitsReminders: boolean;
}

export function defaultCaregiverNotificationSettings(): CaregiverNotificationSettings {
  return {
    pushNotificationsEnabled: true,
    categories: {
      newVisitsAvailable: true,
      agencyNotifications: true,
      chatMessages: true,
      trainingCenterReminders: true,
      myVisitsChanges: true,
      myVisitsReminders: true,
    },
  };
}

/**
 * @deprecated Use `CaregiverNotificationSettings`
 */
export interface NotificationSettings {
  receiveVisitNotifications: boolean;
}

export interface PushNotificationInfo {
  id: CaregiverNotificationId;
  pushNotification: NotificationData;

  createdAt: Instant;
  viewed: boolean;
  clicked: boolean;
  notificationText: string;
}

export interface PushNotificationInfoList {
  pushNotificationInfos: PushNotificationInfo[];
}

export namespace NotificationData {
  export interface CaregiverStatusChanged {
    type: "CAREGIVER_STATUS_CHANGED";
    agencyId: AgencyId;
  }

  export interface AgencyMemberChatMessage {
    type: "AGENCY_MEMBER_CHAT_MESSAGE";
    chatRoomId: ChatRoomId;
  }

  export interface PatientChatMessage {
    type: "PATIENT_CHAT_MESSAGE";
    chatRoomId: ChatRoomId;
    patientId: PatientId;
  }

  export interface VisitChatMessage {
    type: "VISIT_CHAT_MESSAGE";
    visitId: VisitBroadcastId;
  }

  export interface VisitStartingSoonReminder {
    type: "VISIT_STARTING_SOON_REMINDER";
    visitId: VisitBroadcastId;
  }

  export interface VisitNotification {
    type: "VISIT_NOTIFICATION";
    visitId: VisitBroadcastId;
    date?: Instant;
  }

  export interface PatientTaskNotification {
    type: "PATIENT_TASK_NOTIFICATION";
    patientTaskInstanceId: PatientTaskInstanceId;
  }

  export interface VisitInstanceNotification {
    type: "VISIT_INSTANCE_NOTIFICATION";
    visitInstanceId: VisitInstanceId;
  }

  export interface VisitAssignmentNotification {
    type: "VISIT_ASSIGNMENT_NOTIFICATION";
    visitId: VisitBroadcastId;
  }

  export interface VisitCancelNotification {
    type: "VISIT_CANCEL_NOTIFICATION";
  }

  export interface ClusterNotification {
    type: "NEW_CLUSTER_NOTIFICATION";
    clusterId: ClusterId;
  }
  export interface AvailabilityAnomalyNotification {
    type: "AVAILABILITY_ANOMALY_NOTIFICATION";
    agencyId: AgencyId;
  }

  export interface OrientationVideoReminderNotification {
    type: "ORIENTATION_VIDEO_REMINDER_NOTIFICATION";
    agencyId: AgencyId;
  }

  export interface ComplianceProblemNotification {
    type: "COMPLIANCE_PROBLEM_NOTIFICATION";
    agencyId: AgencyId;
    complianceItem: CaregiverDocumentTypeId;
  }

  export interface VisitAwaitingPatientContact {
    type: "VISIT_AWAITING_PATIENT_CONTACT";
    visitId: VisitBroadcastId;
  }

  export interface CaregiverGotRecruited {
    type: "CAREGIVER_GOT_RECRUITED";
    agencyId: AgencyId;
  }

  export interface RecruitmentApplicationReminder {
    type: "RECRUITMENT_APPLICATION_REMINDER_NOTIFICATION";
  }

  export interface CaregiverMissingDocumentReminder {
    type: "CAREGIVER_MISSING_DOCUMENTS_REMINDER";
    scheduledVisitId: VisitInstanceId;
  }

  export interface PatientDocumentUnsubmittedRemainder {
    type: "PATIENT_DOCUMENT_UNSUBMITTED_REMINDER_NOTIFICATION";
  }

  export interface Covid19TrainingReminder {
    type: "COVID_19_TRAINING_REMINDER";
  }

  export interface Covid19ScreeningReminder {
    type: "COVID_19_SCREENING_REMINDER";
  }

  export interface Covid19VisitScreeningReminder {
    type: "COVID_19_VISIT_SCREENING_REMINDER";
  }

  export interface CaregiverPassportDocumentVerificationStatusChanged {
    type: "CAREGIVER_PASSPORT_DOCUMENT_VERIFICATION_STATUS_CHANGED";
  }

  export interface ConvertImportedVisitRequestResult {
    type: "CONVERT_IMPORTED_VISIT_REQUEST_RESULT";
    success: boolean;
    possiblePatients: PatientId[];
    multipleCandidates: boolean;
    noImportedVisit: boolean;
    noCaregiver: boolean;
    agencyId: AgencyId | null;
  }

  export interface CaregiverInactiveFor3Weeks {
    type: "CAREGIVER_INACTIVE_FOR_3_WEEKS";
  }

  export interface FormsAwaitingSubmissionTask {
    type: "FORMS_AWAITING_SUBMISSION_TASK";
  }

  export interface TaskWasntScheduled {
    type: "TASK_WASNT_SCHEDULED";
  }

  export interface MissedClockIn {
    type: "MISSED_CLOCK_IN";
  }

  export interface MissedClockOut {
    type: "MISSED_CLOCK_OUT";
  }

  export interface FormsAwaitingSubmissionVisit {
    type: "FORMS_AWAITING_SUBMISSION_VISIT";
  }
  export interface ExpiredComplianceItem {
    type: "EXPIRED_COMPLIANCE_ITEM";
  }
  export interface AboutToExpireComplianceItem {
    type: "ABOUT_TO_EXPIRE_COMPLIANCE_ITEM";
  }
  export interface CaregiverVisitNotSeen {
    type: "CAREGIVER_VISIT_NOT_SEEN";
  }

  export interface PatientOnVacation {
    type: "PATIENT_ON_VACATION";
  }

  export interface DutySheetSubmissionReminder {
    type: "DUTY_SHEET_SUBMISSION_REMINDER";
  }

  export interface OnboardingReminder {
    type: "ONBOARDING_REMINDER";
  }

  export interface NewOnboardingVisits {
    type: "NEW_ONBOARDING_VISITS";
  }

  export interface OnboardingDocumentNotification {
    type: "ONBOARDING_DOCUMENT_NOTIFICATION";
  }

  export interface CommCenterTicketMessage {
    type: "COMM_CENTER_TICKET_MESSAGE";
    ticketId: CommCenterTicketId;
  }

  /**
   * @deprecated use OnboardingCaregiverEngagements2
   */
  export interface OnboardingCaregiverEngagements {
    type: "ONBOARDING_CAREGIVER_ENGAGEMENTS";
  }

  export interface OnboardingCaregiverEngagements2 {
    type: "ONBOARDING_CAREGIVER_ENGAGEMENTS2";
    payload: OnboardingCaregiversNotificationPayloads[keyof OnboardingCaregiversNotificationPayloads];
  }

  export interface GeneralVisitAssignmentNotification {
    type: "GENERAL_VISIT_ASSIGNMENT_NOTIFICATION";
  }

  export interface InstaPayFailureNotification {
    type: "INSTA_PAY_FAILURE_NOTIFICATION";
  }

  export interface VisitBroadcastStoppedByAgencyMember {
    type: "VISIT_BROADCAST_STOPPED_BY_AGENCY_MEMBER";
  }

  export interface FlexibleVisitBroadcastDetailsChanged {
    type: "FLEXIBLE_VISIT_BROADCAST_DETAILS_CHANGED";
    visitId: VisitBroadcastId;
  }

  export interface CareMomentsCaregiverRankAdjusted {
    type: "CARE_MOMENTS_CAREGIVER_RANK_ADJUSTED";
    payload: CareMomentsCaregiverRankAdjustedPayload;
  }

  export interface RaffleEngagement {
    type: "RAFFLE_ENGAGEMENT";
  }
}

interface CareMomentsCaregiverRankAdjustedPayload {
  adjustment: {
    id: CareMomentsAdjustmentId;
  };
}
